import { useCallback, useEffect, useState } from 'react';
import type { AdminUserListItem } from '@/types/api';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { listUsers } from '../api/admin-users-api';
import { AddUserDialog } from './add-user-dialog';
import { EditUserDialog } from './edit-user-dialog';
import { DeactivateUserConfirm } from './deactivate-user-confirm';
import { ResetPasswordConfirm } from './reset-password-confirm';

function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function LoadingSkeleton() {
  return (
    <div data-testid="admin-users-loading" className="space-y-3" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-gray-700/60" />
      ))}
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div
      data-testid="admin-users-error"
      role="alert"
      className="rounded border border-red-500 bg-red-500/10 px-4 py-3 text-red-400"
    >
      {message}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="admin-users-empty"
      className="rounded border border-dashed border-gray-700 bg-gray-800/40 px-4 py-12 text-center text-gray-400"
    >
      No users yet.
    </div>
  );
}

interface UsersTableProps {
  users: AdminUserListItem[];
  currentAdminId: number;
  onEdit: (user: AdminUserListItem) => void;
  onResetPassword: (user: AdminUserListItem) => void;
  onDeactivate: (user: AdminUserListItem) => void;
}

function UsersTable({ users, currentAdminId, onEdit, onResetPassword, onDeactivate }: UsersTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-700 bg-gray-800">
      <table data-testid="admin-users-table" className="min-w-full divide-y divide-gray-700">
        <thead className="bg-gray-800">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
              Email
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
              Name
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
              Role
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
              Created
            </th>
            <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {users.map((u) => (
            <tr
              key={u.id}
              data-testid={`admin-users-row-${u.id}`}
              className={u.isActive ? 'text-gray-100' : 'text-gray-400 opacity-60'}
            >
              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium">{u.email}</td>
              <td className="whitespace-nowrap px-4 py-3 text-sm">{u.fullName}</td>
              <td className="whitespace-nowrap px-4 py-3 text-sm">{u.role}</td>
              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-400">
                {formatCreatedAt(u.createdAt)}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    data-testid={`admin-users-edit-button-${u.id}`}
                    onClick={() => onEdit(u)}
                    className="rounded border border-gray-600 px-3 py-1 text-xs font-medium text-gray-200 hover:bg-gray-700"
                  >
                    {u.isActive ? 'Edit' : 'Reactivate'}
                  </button>
                  {u.isActive && u.id !== currentAdminId && (
                    <button
                      type="button"
                      data-testid={`admin-users-reset-button-${u.id}`}
                      onClick={() => onResetPassword(u)}
                      className="rounded border border-gray-600 px-3 py-1 text-xs font-medium text-gray-200 hover:bg-gray-700"
                    >
                      Reset password
                    </button>
                  )}
                  {u.isActive && (
                    <button
                      type="button"
                      data-testid={`admin-users-deactivate-button-${u.id}`}
                      onClick={() => onDeactivate(u)}
                      className="rounded border border-red-700 px-3 py-1 text-xs font-medium text-red-300 hover:bg-red-900/40"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  // Default to -1 so no row is ever accidentally flagged as "self" if the
  // auth context hasn't populated yet (shouldn't happen behind AdminRoute).
  const currentAdminId = currentUser?.id ?? -1;

  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<AdminUserListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUserListItem | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUserListItem | null>(null);

  const refetch = useCallback(async () => {
    try {
      const data = await listUsers();
      setUsers(data);
      setError(null);
    } catch {
      setError('Could not load users. Check API connectivity and try again.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listUsers();
        if (!cancelled) {
          setUsers(data);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load users. Check API connectivity and try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddUserClick = () => setIsAddOpen(true);
  const handleAddClose = () => setIsAddOpen(false);
  const handleAddCreated = () => {
    void refetch();
  };

  const handleEditClick = (user: AdminUserListItem) => setEditTarget(user);
  const handleEditClose = () => setEditTarget(null);
  const handleEditSaved = () => {
    void refetch();
    setEditTarget(null);
  };

  const handleDeactivateClick = (user: AdminUserListItem) => setDeactivateTarget(user);
  const handleDeactivateClose = () => setDeactivateTarget(null);
  const handleDeactivateConfirmed = () => {
    void refetch();
    setDeactivateTarget(null);
  };

  const handleResetClick = (user: AdminUserListItem) => setResetTarget(user);
  const handleResetClose = () => setResetTarget(null);
  const handleResetConfirmed = () => {
    // Refetch to update the user's `mustChangePassword` flag in the list (cosmetic;
    // the table doesn't render it today but other consumers/Edit dialog may).
    void refetch();
    setResetTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Users</h1>
        <button
          type="button"
          onClick={handleAddUserClick}
          data-testid="admin-users-add-button"
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
        >
          Add User
        </button>
      </div>

      {isLoading && <LoadingSkeleton />}
      {!isLoading && error && <ErrorMessage message={error} />}
      {!isLoading && !error && users.length === 0 && <EmptyState />}
      {!isLoading && !error && users.length > 0 && (
        <UsersTable
          users={users}
          currentAdminId={currentAdminId}
          onEdit={handleEditClick}
          onResetPassword={handleResetClick}
          onDeactivate={handleDeactivateClick}
        />
      )}

      <AddUserDialog open={isAddOpen} onCreated={handleAddCreated} onClose={handleAddClose} />

      {editTarget && (
        <EditUserDialog
          key={`edit-${editTarget.id}`}
          open={editTarget !== null}
          user={editTarget}
          currentAdminId={currentAdminId}
          onSaved={handleEditSaved}
          onClose={handleEditClose}
        />
      )}

      {deactivateTarget && (
        <DeactivateUserConfirm
          key={`deactivate-${deactivateTarget.id}`}
          open={deactivateTarget !== null}
          user={deactivateTarget}
          currentAdminId={currentAdminId}
          onConfirmed={handleDeactivateConfirmed}
          onClose={handleDeactivateClose}
        />
      )}

      {resetTarget && (
        <ResetPasswordConfirm
          key={`reset-${resetTarget.id}`}
          open={resetTarget !== null}
          user={resetTarget}
          onConfirmed={handleResetConfirmed}
          onClose={handleResetClose}
        />
      )}
    </div>
  );
}
